// Teklifler sayfası - Teklif listesi ve yönetimi
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  FiPlus, 
  FiSearch, 
  FiEdit, 
  FiTrash2, 
  FiDownload,
  FiFileText,
  FiLock,
  FiX
} from 'react-icons/fi';
import { IoFilterCircleOutline } from 'react-icons/io5';
import { CgMenuGridO } from 'react-icons/cg';
import * as offerService from '../../services/offerService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import DeleteConfirmModal from '../../components/Common/DeleteConfirmModal';
import CurrencyInput from 'react-currency-input-field';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

const Offers = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const [filters, setFilters] = useState({ customer: '', project: '', status: [], created_by: [], date: '' });
  const [tempFilters, setTempFilters] = useState({ customer: '', project: '', created_by: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  
  // Düzenleme modal state'leri
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerDescription, setOfferDescription] = useState('');
  const [offerTotal, setOfferTotal] = useState('');
  const [offerProfitability, setOfferProfitability] = useState('');
  const [offerCurrency, setOfferCurrency] = useState('TRY');
  const [offerStatus, setOfferStatus] = useState('Hazırlandı');
  const [offerDate, setOfferDate] = useState('');
  const [offerTime, setOfferTime] = useState('');
  const [offerFile, setOfferFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Sıralama ve filtreleme state'leri
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFilters, setShowFilters] = useState({ customer: false, project: false, status: false, created_by: false, date: false });
  const [showActionMenu, setShowActionMenu] = useState(null);

  // Teklif durumu metinleri
  const getStatusText = (status) => {
    // Artık veritabanından Türkçe değerler geliyor, direkt döndür
    return status || 'Bilinmeyen';
  };

  // Durum seçenekleri
  const statusOptions = [
    'Hazırlandı',
    'Gönderildi', 
    'Müşteri Onay Sürecinde',
    'Müşteri Revize İstedi',
    'Teklif Kabul Edilmedi',
    'Teklif Onaylandı',
    'Revize Gönderilecek'
  ];

  // Durum renkleri
  const getStatusColor = (status) => {
    switch (status) {
      case 'Hazırlandı': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'Gönderildi': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'Müşteri Onay Sürecinde': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'Müşteri Revize İstedi': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      case 'Teklif Kabul Edilmedi': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'Teklif Onaylandı': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'Revize Gönderilecek': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Para birimi formatı
  const formatCurrency = (amount, currency) => {
    // Açıklama: currency bazı kayıtlarda 'currency', bazılarında 'offer_currency' olabilir; 'TL' -> 'TRY' normalize edilir
    const cur = currency || 'TRY';
    const currencyCode = cur === 'TL' ? 'TRY' : cur;
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(amount || 0));
  };

  // Teklif oluşturan kullanıcıları al (mevcut filtrelere göre)
  const { data: creatorsData } = useQuery(
    ['offerCreators', searchTerm, filters.status, filters.customer, filters.project, filters.date],
    () => offerService.getOfferCreators({
      search: searchTerm,
      status: filters.status && filters.status.length > 0 ? filters.status.join(',') : '',
      customer: filters.customer,
      project: filters.project,
      date: filters.date,
    }),
    { keepPreviousData: true }
  );

  // Teklif listesini al
  const { data: offersData, isLoading } = useQuery(
    ['offers', currentPage, searchTerm, filters, itemsPerPage],
    () => offerService.getOffers({
      page: currentPage,
      search: searchTerm,
      status: filters.status && filters.status.length > 0 ? filters.status.join(',') : '',
      customer: filters.customer,
      project: filters.project,
      created_by: (filters.created_by && filters.created_by.length > 0) ? filters.created_by.join(',') : '',
      date: filters.date,
      limit: itemsPerPage
    }),
    { keepPreviousData: true }
  );

  // Teklif silme mutation'ı
  const deleteOfferMutation = useMutation(
    offerService.deleteOffer,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('offers');
        toast.success('Teklif başarıyla silindi');
        setShowDeleteModal(false);
        setSelectedOffer(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Teklif silinemedi');
      }
    }
  );

  // Silme fonksiyonları
  const handleDeleteOffer = (offer) => {
    setSelectedOffer(offer);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (selectedOffer) {
      deleteOfferMutation.mutate(selectedOffer.id);
    }
  };

  // Teklif düzenleme fonksiyonu
  const handleEditOffer = (offer) => {
    setEditingOffer(offer);
    setOfferDescription(offer.offer_file_description || offer.offer_description || '');
    setOfferTotal(offer.offer_total || offer.offer_amount || '');
    setOfferProfitability(offer.profitability || '');
    setOfferCurrency(offer.currency || offer.offer_currency || 'TRY');
    setOfferStatus(offer.offer_status || 'Hazırlandı');
    setOfferDate(offer.created_at ? offer.created_at.split('T')[0] : '');
    setOfferTime(offer.created_at ? offer.created_at.split('T')[1]?.substring(0, 5) : '');
    setOfferFile(null);
    setShowEditModal(true);
    setShowActionMenu(null);
  };

  // Teklif güncelleme fonksiyonu
  const handleUpdateOffer = async () => {
    if (!editingOffer) return;
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('offer_description', offerDescription);
      formData.append('offer_total', offerTotal);
      formData.append('profitability', offerProfitability);
      formData.append('currency', offerCurrency);
      formData.append('offer_status', offerStatus);
      
      if (offerDate && offerTime) {
        const dateTime = `${offerDate}T${offerTime}:00`;
        formData.append('created_at', dateTime);
      }
      
      if (offerFile) {
        formData.append('offer_file', offerFile);
      }

      await offerService.updateOffer(editingOffer.id, formData);
      
      queryClient.invalidateQueries('offers');
      toast.success('Teklif başarıyla güncellendi');
      setShowEditModal(false);
      setEditingOffer(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Teklif güncellenemedi');
    } finally {
      setIsUploading(false);
    }
  };

  // Geçici arama terimi değişikliği (sadece input'u günceller)
  const handleTempSearchChange = (e) => {
    setTempSearchTerm(e.target.value);
  };

  // Enter tuşuna basınca aramayı uygula
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearchTerm(tempSearchTerm);
      setCurrentPage(1);
    }
  };

  // Filtre fonksiyonu
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Multi-select durum filtresi
  const handleStatusToggle = (status) => {
    setFilters(prev => {
      const currentStatuses = prev.status || [];
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter(s => s !== status)
        : [...currentStatuses, status];
      return { ...prev, status: newStatuses };
    });
    setCurrentPage(1);
  };

  // Geçici filtre değişikliği (sadece input'u günceller)
  const handleTempFilterChange = (type, value) => {
    setTempFilters(prev => ({ ...prev, [type]: value }));
  };

  // Enter tuşuna basınca filtreyi uygula
  const handleFilterKeyDown = (type, e) => {
    if (e.key === 'Enter') {
      setFilters(prev => ({ ...prev, [type]: tempFilters[type] }));
      setCurrentPage(1);
      // Enter'a basınca filtre inputunu kapat
      setShowFilters(prev => ({ ...prev, [type]: false }));
    }
  };

  // Sayfa başına öğe sayısı değiştirme
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  // Sıralama fonksiyonu
  const handleSort = (key) => {
    setSortConfig(prev => ({ 
      key, 
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' 
    }));
  };

  // Filtre toggle fonksiyonu
  const toggleFilter = (key) => {
    setShowFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Action menu toggle fonksiyonu
  const toggleActionMenu = (id, event) => {
    if (showActionMenu === id) {
      setShowActionMenu(null);
    } else {
      setShowActionMenu(id);
      setTimeout(() => {
        const button = event.target.closest('button');
        if (button) {
          const rect = button.getBoundingClientRect();
          const menu = document.querySelector('.action-menu-dropdown');
          if (menu) {
            menu.style.left = `${rect.right - 192}px`;
            menu.style.top = `${rect.bottom + 4}px`;
          }
        }
      }, 0);
    }
  };

  // Sayfa değiştirme
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Action menu dışında tıklama kontrolü
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionMenu && !event.target.closest('.action-menu')) {
        setShowActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionMenu]);

  // URL parametrelerini okuyup filtreleme yap
  React.useEffect(() => {
    const status = searchParams.get('status');
    const created_by = searchParams.get('created_by');
    
    if (status) {
      // Virgülle ayrılmış durumları ayır
      const statusArray = status.split(',');
      setFilters(prev => ({ ...prev, status: statusArray }));
    }
    
    if (created_by) {
      // Virgülle ayrılmış kullanıcı ID'lerini ayır
      const createdByArray = created_by.split(',').map(id => id.trim()).filter(id => id !== '');
      setFilters(prev => ({ ...prev, created_by: createdByArray }));
    }
  }, [searchParams]);

  // Filtreler değiştiğinde tempFilters'ı senkronize et
  React.useEffect(() => {
    setTempFilters({
      customer: filters.customer,
      project: filters.project,
      created_by: filters.created_by
    });
  }, [filters.customer, filters.project]);

  // searchTerm değiştiğinde tempSearchTerm'i senkronize et
  React.useEffect(() => {
    setTempSearchTerm(searchTerm);
  }, [searchTerm]);

  // Tablo dışına tıklayınca filtreleri kapat
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Eğer tıklanan element tablo veya filtre içinde değilse filtreleri kapat
      const tableElement = document.querySelector('.offers-table');
      if (tableElement && !tableElement.contains(event.target)) {
        setShowFilters({ customer: false, project: false, status: false, created_by: false, date: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Excel export fonksiyonu
  const handleExportExcel = () => {
    
    // Veri yapısını kontrol et
    let dataToExport = offersData;
    if (offersData && typeof offersData === 'object' && !Array.isArray(offersData)) {
      // Eğer object ise, data property'sini kontrol et
      if (offersData.data && Array.isArray(offersData.data)) {
        dataToExport = offersData.data;
      } else if (offersData.offers && Array.isArray(offersData.offers)) {
        dataToExport = offersData.offers;
      }
    }
    
    
    if (!dataToExport || !Array.isArray(dataToExport) || dataToExport.length === 0) {
      toast.error('Export edilecek teklif bulunamadı');
      return;
    }

    const exportData = dataToExport.map(offer => ({
      'Teklif Başlığı': offer.offer_file_description || '-',
      'Müşteri': offer.customer_name || '-',
      'Proje': offer.project_name || '-',
      'Tutar': `${offer.offer_total || 0} ${(offer.currency || offer.offer_currency || 'TRY')}`,
      'Durum': offer.offer_status,
      'Oluşturan': offer.created_by_name || '-',
      'Oluşturulma Tarihi': new Date(offer.created_at).toLocaleDateString('tr-TR')
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Teklifler');

    const fileName = `teklifler_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    toast.success('Excel dosyası başarıyla indirildi');
  };

  // Aktif filtre kontrolü
  const hasActiveFilters = () => {
    return searchTerm || filters.customer || filters.project || (filters.status && filters.status.length > 0) || (filters.created_by && filters.created_by.length > 0) || filters.date;
  };

  // Tüm filtreleri temizle
  const clearAllFilters = () => {
    setSearchTerm('');
    setTempSearchTerm('');
    setFilters({ customer: '', project: '', status: [], created_by: [], date: '' });
    setTempFilters({ customer: '', project: '', created_by: [] });
    setShowFilters({ customer: false, project: false, status: false, created_by: false, date: false });
    setCurrentPage(1);
  };

  // Filtrelenmiş ve sıralanmış teklifler
  const filteredOffers = useMemo(() => {
    if (!offersData?.data) return [];
    
    let filtered = [...offersData.data];
    
    // Backend zaten filtreleme yapıyor, frontend filtreleme gereksiz
    
    // Sıralama uygula
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Null/undefined değerleri kontrol et
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';
        
        // String değerler için
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase().trim();
          bValue = bValue.toLowerCase().trim();
        }
        
        // Sayısal değerler için
        if (sortConfig.key === 'offer_total' || sortConfig.key === 'offer_amount' || sortConfig.key === 'profitability') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // Tarih için
        if (sortConfig.key === 'created_at') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
        
        // ID için (teklif numarası)
        if (sortConfig.key === 'id') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [offersData, sortConfig]);

  // Sayfalama hesaplamaları
  const pagination = {
    currentPage,
    totalPages: Math.ceil((offersData?.total || 0) / itemsPerPage),
    totalItems: offersData?.total || 0
  };

  // Mevcut tablodaki tekliflerin toplam tutarları - para birimine göre ayrı
  const tableStats = useMemo(() => {
    const init = {
      TRY: { amount: 0, profit: 0, count: 0 },
      USD: { amount: 0, profit: 0, count: 0 },
      EUR: { amount: 0, profit: 0, count: 0 },
      totalCount: 0,
    };
    if (!filteredOffers || filteredOffers.length === 0) return init;

    return filteredOffers.reduce((agg, offer) => {
      const curRaw = (offer.currency || offer.offer_currency || 'TRY');
      const key = curRaw === 'USD' ? 'USD' : (curRaw === 'EUR' ? 'EUR' : (curRaw === 'TL' ? 'TRY' : 'TRY'));
      const amount = Number(offer.offer_total) || Number(offer.offer_amount) || 0;
      const profit = Number(offer.profitability) || 0;
      agg[key].amount += amount;
      agg[key].profit += profit;
      agg[key].count += 1;
      agg.totalCount += 1;
      return agg;
    }, init);
  }, [filteredOffers]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center text-gray-600 dark:text-gray-400">
          <FiFileText size={24} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Teklifler</h1>
      </div>

      {/* Arama ve Export */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Arama */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Teklif ara... (Enter ile ara)"
              value={tempSearchTerm}
              onChange={handleTempSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="input-field pl-10"
            />
          </div>

          <button 
            onClick={handleExportExcel}
            className="btn-secondary flex items-center justify-center px-4 py-2"
          >
            <FiDownload className="mr-2" size={16} />
            Excel
          </button>
        </div>
      </div>

      {/* Filtre Uyarı Mesajı */}
      {hasActiveFilters() && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Filtre uygulanmıştır
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  {searchTerm && `Arama: "${searchTerm}"`}
                  {filters.customer && ` • Müşteri: "${filters.customer}"`}
                  {filters.project && ` • Proje: "${filters.project}"`}
                  {filters.status && filters.status.length > 0 && ` • Durum: "${filters.status.join(', ')}"`}
                  {filters.created_by && filters.created_by.length > 0 && ` • Oluşturan: ${filters.created_by.length} seçildi`}
                  {filters.date && ` • Tarih: "${filters.date}"`}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 dark:text-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FiX className="mr-1" size={14} />
                Tüm Filtreleri Temizle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teklif listesi - Mobil 4 sütun grid (sm altında), her satırda tek teklif */}
      <div className="card p-4 sm:p-6 sm:hidden">
        {/* Açıklama: Mobilde tablo yerine 4 sütunlu satır bazlı grid gösterilir */}
        <div className="grid grid-cols-4 gap-2 text-[10px] font-semibold text-gray-500 dark:text-gray-400 px-1">
          <div>Teklif</div>
          <div>Müşteri</div>
          <div>Durum</div>
          <div>Tarih</div>
        </div>
        <div className="mt-2">
          {filteredOffers.length > 0 ? (
            filteredOffers.map((offer) => (
              <div
                key={offer.id}
                onClick={() => window.location.href = `/offers/${offer.id}`}
                className="grid grid-cols-4 gap-2 items-center px-2 py-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="pr-2">
                  <div className="text-[11px] text-gray-900 dark:text-white font-medium truncate">
                    #{offer.offer_number || offer.id}
                  </div>
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
                    {offer.offer_title || offer.offer_file_description || '-'}
                  </div>
                </div>
                <div className="text-[11px] text-gray-700 dark:text-gray-300 truncate">
                  {offer.customer_name || '-'}
                </div>
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(offer.offer_status)}`}>
                    {getStatusText(offer.offer_status)}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 text-right">
                  {new Date(offer.created_at).toLocaleDateString('tr-TR')}
                </div>
              </div>
            ))
          ) : (
            <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Henüz teklif bulunmuyor</div>
          )}
        </div>

        {/* Mobil sayfalama: Önceki/Sonraki ve mevcut sayfa bilgisi */}
        {pagination.totalPages > 1 && (
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <button
                onClick={() => handlePageChange(Math.max(1, Number(currentPage) - 1))}
                disabled={Number(currentPage) <= 1}
                className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                  Number(currentPage) <= 1
                    ? 'text-gray-400 border-gray-200 dark:text-gray-600 dark:border-gray-700 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-brand-primary hover:text-white'
                }`}
              >
                Önceki
              </button>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Sayfa {pagination.currentPage} / {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(Math.min(Number(pagination.totalPages) || 1, Number(currentPage) + 1))}
                disabled={Number(currentPage) >= Number(pagination.totalPages)}
                className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                  Number(currentPage) >= Number(pagination.totalPages)
                    ? 'text-gray-400 border-gray-200 dark:text-gray-600 dark:border-gray-700 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-brand-primary hover:text-white'
                }`}
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Teklif Tablosu - Masaüstü (sm ve üzeri) */}
      <div className="card hidden sm:block">
        <div className="table-container overflow-x-auto">
          <table className="table w-full table-fixed text-xs offers-table">
            <thead>
              <tr>
                <th className="relative w-[7%]">
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleSort('id')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      TEKLİF NO
                      {sortConfig.key === 'id' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </div>
                </th>
                <th className="relative w-[13%]">
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleSort('customer_name')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      MÜŞTERİ ADI
                      {sortConfig.key === 'customer_name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={() => toggleFilter('customer')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.customer && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="text" 
                        placeholder="Filtrele... (Enter ile uygula)" 
                        value={tempFilters.customer} 
                        onChange={(e) => handleTempFilterChange('customer', e.target.value)} 
                        onKeyDown={(e) => handleFilterKeyDown('customer', e)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
                <th className="relative w-[13%]">
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleSort('project_name')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      PROJE ADI
                      {sortConfig.key === 'project_name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={() => toggleFilter('project')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.project && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="text" 
                        placeholder="Filtrele... (Enter ile uygula)" 
                        value={tempFilters.project} 
                        onChange={(e) => handleTempFilterChange('project', e.target.value)} 
                        onKeyDown={(e) => handleFilterKeyDown('project', e)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
                <th className="w-[8%]">
                  <button onClick={() => handleSort('offer_total')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                    TEKLİF TUTARI
                    {sortConfig.key === 'offer_total' && (
                      <span className="ml-1">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="w-[8%]">
                  <button onClick={() => handleSort('profitability')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                    TEKLİF KARLILIĞI
                    {sortConfig.key === 'profitability' && (
                      <span className="ml-1">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="relative w-[10%]">
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleSort('offer_status')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      TEKLİF DURUMU
                      {sortConfig.key === 'offer_status' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={() => toggleFilter('status')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.status && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2 min-w-[200px]">
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {statusOptions.map((status) => (
                          <label key={status} className="flex items-center space-x-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filters.status.includes(status)}
                              onChange={() => handleStatusToggle(status)}
                              className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span className="text-xs text-gray-700 dark:text-gray-300">{status}</span>
                          </label>
                        ))}
                      </div>
                      {filters.status.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <button
                            onClick={() => setFilters(prev => ({ ...prev, status: [] }))}
                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Tümünü Temizle
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </th>
                <th className="relative w-[10%]">
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleSort('created_by_name')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      TEKLİF OLUŞTURAN
                      {sortConfig.key === 'created_by_name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={() => toggleFilter('created_by')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.created_by && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2 min-w-[200px]">
                      <div className="space-y-1 max-h-60 overflow-y-auto">
                        {creatorsData?.creators && creatorsData.creators.length > 0 ? (
                          creatorsData.creators.map((user) => (
                            <label key={user.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.created_by.includes(String(user.id))}
                                onChange={() => handleFilterChange('created_by', filters.created_by.includes(String(user.id)) ? filters.created_by.filter(v=>v!==String(user.id)) : [...filters.created_by, String(user.id)])}
                                className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                              />
                              <span className="text-xs text-gray-700 dark:text-gray-300">{user.user_fullname}</span>
                            </label>
                          ))
                        ) : (
                          <div className="text-xs text-gray-500 dark:text-gray-400 p-2">Listelenecek kullanıcı yok</div>
                        )}
                      </div>
                      {filters.created_by.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <button
                            onClick={() => handleFilterChange('created_by', [])}
                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Tümünü Temizle
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </th>
                <th className="relative w-[8%]">
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleSort('created_at')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      TEKLİF TARİHİ
                      {sortConfig.key === 'created_at' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={() => toggleFilter('date')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.date && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="date" 
                        value={filters.date} 
                        onChange={(e) => handleFilterChange('date', e.target.value)} 
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
                <th className="w-[6%]"></th>
              </tr>
            </thead>
            <tbody>
              {filteredOffers.length > 0 ? (
                filteredOffers.map((offer) => (
                  <tr key={offer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => window.location.href = `/offers/${offer.id}`}>
                    <td className="text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <FiFileText className="text-gray-400 mr-2" size={16} />
                        <div>
                          <div className="font-medium">
                            #{offer.offer_number || offer.id}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {offer.offer_title}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      <div>
                        {offer.customer_name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {offer.customer_company}
                      </div>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        {offer.project_name && offer.project_visibility === 'Private' && (
                          <FiLock className="mr-2 text-gray-500" size={14} title="Gizli Proje" />
                        )}
                        {offer.project_name || '-'}
                      </div>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {formatCurrency(offer.offer_total || offer.offer_amount, (offer.currency || offer.offer_currency))}
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {(() => {
                        const offerCur = (offer.currency || offer.offer_currency || 'TRY');
                        const currency = offerCur === 'USD' ? 'USD' : (offerCur === 'EUR' ? 'EUR' : 'TRY');
                        const profit = Number(offer.profitability || 0);
                        return profit > 0
                          ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(profit)
                          : '-';
                      })()}
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(offer.offer_status)}`}>
                        {getStatusText(offer.offer_status)}
                      </span>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {offer.created_by_name || '-'}
                    </td>
                    <td className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(offer.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="text-center w-20" onClick={(e) => e.stopPropagation()}>
                      <div className="relative action-menu">
                        {(currentUser?.user_type === 'admin' || 
                          currentUser?.user_type === 'manager' || 
                          offer.created_by === currentUser?.id) && (
                          <button 
                            onClick={(e) => toggleActionMenu(offer.id, e)} 
                            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 p-2 -m-2" 
                            title="İşlemler"
                          >
                            <CgMenuGridO size={18} />
                          </button>
                        )}
                        {showActionMenu === offer.id && (
                          <div className="action-menu-dropdown fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl z-[9999] min-w-48">
                            <div className="py-1">
                              <button 
                                onClick={(e) => {e.stopPropagation(); handleEditOffer(offer); setShowActionMenu(null);}} 
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <FiEdit className="mr-3" size={16} /> Düzenle
                              </button>
                              <button 
                                onClick={(e) => {e.stopPropagation(); handleDeleteOffer(offer); setShowActionMenu(null);}} 
                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <FiTrash2 className="mr-3" size={16} /> Sil
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <FiFileText size={48} className="text-gray-300 dark:text-gray-600" />
                      <p className="text-gray-500 dark:text-gray-400">Henüz teklif bulunmuyor</p>
                    
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sayfalama */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            {/* Sol: Show records */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">records</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
                {pagination.totalItems || 0} kayıt bulundu
              </div>
            </div>
            
            {/* Sağ: Para birimine göre toplam tutarlar - tag görünümlü, alt alta (sağa hizalı, biraz daha geniş) */}
            <div className="w-full flex justify-end">
              <div className="inline-block min-w-[420px] max-w-[560px] w-full sm:w-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                {/* Başlık satırı */}
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 rounded-t-lg">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Toplamlar</span>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {/* TRY satırı */}
                  <div className="flex items-center justify-between px-4 py-3">
                    {tableStats.TRY.count > 0 ? (
                      <>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-700">TL</span>
                        <div className="flex-1 flex items-center justify-end gap-6 text-sm">
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Toplam:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.TRY.amount, 'TRY')}</span>
                          </div>
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Kârlılık:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.TRY.profit, 'TRY')}</span>
                            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({tableStats.TRY.amount > 0 ? ((tableStats.TRY.profit / tableStats.TRY.amount) * 100).toFixed(1) : '0.0'}%)</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full text-sm font-medium text-red-600 dark:text-red-400">TL teklif oluşturulmamıştır</div>
                    )}
                  </div>
                  {/* USD satırı */}
                  <div className="flex items-center justify-between px-4 py-3">
                    {tableStats.USD.count > 0 ? (
                      <>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-700">USD</span>
                        <div className="flex-1 flex items-center justify-end gap-6 text-sm">
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Toplam:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.USD.amount, 'USD')}</span>
                          </div>
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Kârlılık:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.USD.profit, 'USD')}</span>
                            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({tableStats.USD.amount > 0 ? ((tableStats.USD.profit / tableStats.USD.amount) * 100).toFixed(1) : '0.0'}%)</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full text-sm font-medium text-red-600 dark:text-red-400">USD teklif oluşturulmamıştır</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sayfa navigasyonu */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-end mt-4">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Sayfa {pagination.currentPage} / {pagination.totalPages}
                </span>
                <div className="flex space-x-1">
                  {/* İlk sayfa */}
                  <button
                    onClick={() => handlePageChange(1)}
                    className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                      currentPage === 1
                        ? 'bg-brand-primary text-white shadow-lg'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                    }`}
                  >
                    1
                  </button>
                  
                  {/* Dinamik ellipsis ve orta sayfalar - tekrarları engelle */}
                  {(() => {
                    const totalPages = Number(pagination.totalPages) || 1;
                    const curr = Number(currentPage) || 1;
                    const middlePages = Array.from(new Set([
                      curr - 1,
                      curr,
                      curr + 1,
                    ].filter(p => p > 1 && p < totalPages)));

                    const showLeftEllipsis = middlePages.length > 0 && Math.min(...middlePages) > 2;
                    const showRightEllipsis = middlePages.length > 0 && Math.max(...middlePages) < (totalPages - 1);

                    return (
                      <>
                        {showLeftEllipsis && (
                          <span className="px-2 py-2 text-sm text-gray-500 font-medium">...</span>
                        )}

                        {middlePages.map((pageNum) => (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                              currentPage === pageNum
                                ? 'bg-brand-primary text-white shadow-lg'
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                            }`}
                          >
                            {pageNum}
                          </button>
                        ))}

                        {showRightEllipsis && (
                          <span className="px-2 py-2 text-sm text-gray-500 font-medium">...</span>
                        )}
                      </>
                    );
                  })()}
                  
                  {/* Son sayfa (1'den farklıysa) */}
                  {pagination.totalPages > 1 && (
                    <button
                      onClick={() => handlePageChange(pagination.totalPages)}
                      className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                        currentPage === pagination.totalPages
                          ? 'bg-brand-primary text-white shadow-lg'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                      }`}
                    >
                      {pagination.totalPages}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Uyarı mesajı - Hesaplanan rakamların gösterilen kayıt ve filtreler sonrasında oluştuğunu belirtir */}
        {tableStats.totalCount > 0 && (
          <div className="px-6 py-3 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800">
            <p className="text-xs text-blue-600 dark:text-blue-300 text-center">
              <span className="font-medium">Not:</span> Yukarıdaki tutarlar sadece mevcut tabloda görünen {tableStats.totalCount} teklife aittir. 
              Filtreleme veya sayfa değişikliği yapıldığında bu tutarlar otomatik olarak güncellenir. Tüm kayıtlara ait değildir.
            </p>
          </div>
        )}
      </div>

      {/* Teklif Düzenleme Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-lg">
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Teklifi Düzenle</h3>
                <button onClick={() => setShowEditModal(false)} className="text-white hover:text-gray-300">
                  <FiX size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Durum</label>
                  <select
                    className="input-field w-full"
                    value={offerStatus}
                    onChange={(e) => setOfferStatus(e.target.value)}
                  >
                    <option>Hazırlandı</option>
                    <option>Gönderildi</option>
                    <option>Müşteri Onay Sürecinde</option>
                    <option>Müşteri Revize İstedi</option>
                    <option>Teklif Kabul Edilmedi</option>
                    <option>Teklif Onaylandı</option>
                    <option>Revize Gönderilecek</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Para Birimi</label>
                  <select
                    className="input-field w-full"
                    value={offerCurrency}
                    onChange={(e) => setOfferCurrency(e.target.value)}
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teklif Tutarı</label>
                  <CurrencyInput
                    id="offerTotal"
                    name="offerTotal"
                    placeholder="0.00"
                    value={offerTotal}
                    onValueChange={(value, name) => {
                      setOfferTotal(value);
                    }}
                    prefix={offerCurrency === 'USD' ? '$' : offerCurrency === 'EUR' ? '€' : '₺'}
                    decimalsLimit={2}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kârlılık</label>
                  <CurrencyInput
                    id="profitability"
                    name="profitability"
                    placeholder="0.00"
                    value={offerProfitability}
                    onValueChange={(value, name) => {
                      setOfferProfitability(value);
                    }}
                    prefix={offerCurrency === 'USD' ? '$' : offerCurrency === 'EUR' ? '€' : '₺'}
                    decimalsLimit={2}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gönderilme Tarihi</label>
                  <input
                    type="date"
                    value={offerDate}
                    onChange={(e) => setOfferDate(e.target.value)}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gönderilme Saati</label>
                  <input
                    type="time"
                    value={offerTime}
                    onChange={(e) => setOfferTime(e.target.value)}
                    className="input-field w-full"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teklif Dosyası (Opsiyonel)</label>
                  <input
                    type="file"
                    onChange={(e) => setOfferFile(e.target.files[0])}
                    className="input-field w-full"
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teklif Açıklaması</label>
                  <textarea
                    value={offerDescription}
                    onChange={(e) => setOfferDescription(e.target.value)}
                    className="input-field w-full"
                    rows={3}
                    placeholder="Teklif açıklaması..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary"
                  disabled={isUploading}
                >
                  İptal
                </button>
                <button
                  onClick={handleUpdateOffer}
                  className="btn-primary"
                  disabled={isUploading}
                >
                  {isUploading ? 'Güncelleniyor...' : 'Güncelle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onay Modal'ı */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Teklifi Sil"
        message={`#${selectedOffer?.id} numaralı teklifi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        isLoading={deleteOfferMutation.isLoading}
      />
    </div>
  );
};

export default Offers;
